import { useMemo } from 'react';

import type { EntrypointId } from './EntrypointId';

export const FEEDBACK_CONTEXT_CF = 'customfield_10047';

type Field = { id: string; value: string };

type AdditionalFieldsProps = {
  contextAttributes?: Record<string, string>;
  contextOverride?: string;
  entrypointId?: EntrypointId;
};

/**
 * Builds the additional fields to include when submitting feedback.
 *
 * Currently, the only additional field produced is the Feedback Context
 * custom field ({@link FEEDBACK_CONTEXT_CF}). When a `contextOverride` is
 * supplied it is used verbatim; otherwise the value is derived by
 * concatenating the `entrypointId` with every entry in `contextAttributes`.
 *
 * @param props - The properties used to construct the additional fields.
 * @param props.contextAttributes - Optional key-value pairs appended to the
 *   feedback context string when no `contextOverride` is provided.
 * @param props.contextOverride - Optional string that, when present, replaces
 *   the auto-generated feedback context value entirely.
 * @param props.entrypointId - Optional identifier for the feedback entrypoint,
 *   used as the leading segment of the generated context string.
 * @returns An array of additional fields, each containing an `id` and `value`.
 */
export const useAdditionalFields = ({
  contextAttributes,
  contextOverride,
  entrypointId,
}: AdditionalFieldsProps): Field[] => {
  // Currently the only additional field we send is Feedback Context
  const additionalFields = useMemo(() => {
    if (contextOverride) {
      return [{ id: FEEDBACK_CONTEXT_CF, value: contextOverride }];
    }
    return [
      {
        id: FEEDBACK_CONTEXT_CF,
        value: Object.entries(contextAttributes ?? {}).reduce(
          (acc, [key, val]) => `${acc}, ${key}: ${val}`,
          `entrypointId: ${entrypointId}`,
        ),
      },
    ];
  }, [contextAttributes, contextOverride, entrypointId]);
  return additionalFields;
};
